import React from 'react';
import { Body, Button, TreeSelect } from '@panwds/react-ui';
import { countUniqueCities } from '../utils/regionCounts';
import RegionChipList from './RegionChipList';
import commonStyles from '../../../scss/StatusPageCommon.module.scss';

/**
 * One plane's region-selection row inside a `SubscribedProductPanel`: an `Overline`
 * plane label + "N of M" selected-vs-available count, a right-aligned "Edit Regions"
 * link that opens the region `TreeSelect`, and the plane's removable region chips
 * beneath.
 *
 * For single-plane products, the parent still renders exactly one `PlaneRegionRow` (for
 * that product's one enabled plane) so the layout stays consistent -- it is simply fed
 * `checkedProducts[type].regions` instead of a `control`/`data` array. See
 * `SubscribedProductPanel` for how `selectedRegions` is resolved.
 */
const PlaneRegionRow = ({
    productType,
    planeType,
    planeData,
    selectedRegions,
    items,
    treeExpanded,
    onTreeExpand,
    isChipsExpanded,
    onToggleChipsExpand,
    onChangeRegions,
    onRemoveRegion,
    rowTestId,
    chipTestIdPrefix,
}) => {
    const availableCount = countUniqueCities(planeData?.locations);
    const selectedCount = selectedRegions?.length || 0;

    return (
        <div
            className="ssmutw-pb-4 ssmutw-flex ssmutw-flex-col ssmutw-gap-2"
            // `--ssmu-card-border`, not `--ssmu-divider-line`: this row sits inside a
            // PANWDS Card, whose surface is lighter than the page bg the divider token
            // was tuned for (see `utils/cardStyles.js`).
            style={{ borderBottom: '1px solid var(--ssmu-card-border)' }}
            data-testid={rowTestId}
        >
            <div className="ssmutw-flex ssmutw-flex-col ssmutw-gap-2 md:ssmutw-flex-row md:ssmutw-items-center md:ssmutw-justify-between">
                <span className="ssmutw-flex ssmutw-items-baseline ssmutw-gap-2">
                    {/*
                      * `Body appearance="secondary"` rather than `Overline`, even though this
                      * is visually an overline. `Overline` hardcodes a bright
                      * `dark:twds-text-steel-gray-100` and exposes no `appearance` prop, so
                      * muting it would mean out-specificity-ing PANWDS. `Body` has a native
                      * secondary appearance, which also makes this label exactly match the
                      * "N of M" count beside it (same component, same appearance) instead of
                      * being a slightly different grey. The uppercase/tracking/semibold
                      * classes reproduce what `Overline size="sm"` applied.
                      */}
                    <Body
                        size="sm"
                        as="span"
                        appearance="secondary"
                        dataTestId="plane-label"
                        addClassName="ssmutw-uppercase ssmutw-tracking-widest ssmutw-font-semibold"
                    >
                        {planeType} Plane
                    </Body>
                    <Body size="sm" as="span" appearance="secondary">{selectedCount} of {availableCount}</Body>
                </span>
                <TreeSelect
                    // No `size` prop: `TreeSelect`'s own `'md'` default is what the Add
                    // Product picker and the Status tab's Products/Regions filters use, and
                    // `size` is what drives the dropdown row text size (`TreeItemLabel` maps
                    // `lg` -> 14px, everything else -> 12px). A stray `size="lg"` here made
                    // this one dropdown's content a step larger than every other dropdown.
                    //
                    // Placement is intentionally left at the `bottom-start` default: the
                    // window-edge clipping fix lives in the `@panwds/react-ui` patch, which
                    // adds the `flip`/`shift`/`size` floating-ui middleware that `TreeSelect`
                    // (alone among the library's popper components) shipped without. Setting
                    // `defaultPlacement="auto-end"` here would instead swap in
                    // `autoPlacement`, which is mutually exclusive with `flip` and does not
                    // constrain height -- so the panel could still be clipped vertically.
                    selectedItem={selectedRegions || []}
                    enableSearch
                    items={items}
                    hasCheckboxes
                    enableSelectAll
                    enableMultiSelect
                    aria-label={`Select ${planeType} plane regions for ${productType}`}
                    portalProps={{ className: commonStyles.regionTreeFilter }}
                    button={
                        // `-mr-2` cancels the `clear` appearance's own horizontal padding so
                        // the label's right edge lines up with the trash column above it,
                        // rather than sitting ~8px short of it.
                        <Button appearance="clear" size="sm" addClassName="-ssmutw-mr-2">
                            Edit Regions
                        </Button>
                    }
                    treeListProps={{
                        expanded: treeExpanded,
                        onExpand: onTreeExpand,
                        onlyParentChecked: false,
                        onlyLeafChecked: true,
                    }}
                    onChange={({ selectedItems }) => onChangeRegions(selectedItems)}
                />
            </div>
            <div className="ssmutw-flex ssmutw-flex-wrap ssmutw-items-center ssmutw-gap-1 md:ssmutw-gap-2">
                <RegionChipList
                    regions={selectedRegions}
                    isExpanded={isChipsExpanded}
                    onToggleExpand={onToggleChipsExpand}
                    onRemove={onRemoveRegion}
                    testIdPrefix={chipTestIdPrefix}
                />
            </div>
        </div>
    );
};

export default React.memo(PlaneRegionRow);
